import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  Control,
  useForm,
  UseFormHandleSubmit,
  UseFormReset,
} from 'react-hook-form';

// types
import { FiltersSchema, Filters, FilterType, SchemaFilterType } from '../types';
import { InputSize } from '../../inputs/types';
import { ButtonType, ButtonSize } from '../../button/types';

// components
import InputText from '../../inputs/input-text';
import Rangepicker from '../../rangepicker/rangepicker';
import Autocomplete from '../../autocomplete/autocomplete';
import AutocompleteMultiple from '../../autocomplete-multiple/autocomplete';
import ButtonPrimary from '../../button/button-primary';
import { SelectItemProps, SelectSize } from '../../select/types';
import { DatePickerMode } from '../../rangepicker/types';
import ButtonPseudo from '../../button/button-pseudo';
import CheckboxFilter from '../../checkbox/checkbox-filter';

import styles from '../table-filter.module.css';

export interface UseFiltersProps {
  filtersSchema: FiltersSchema[];
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
  onSearchAutocomplete?: (name: string, search: string) => void;
  loaders?: Record<string, boolean>;
  selectFunctions?: Record<
    string,
    {
      selectedList: SelectItemProps[];
      setSelectedList: (data: SelectItemProps[]) => void;
    }
  >;
  onScrollFunctions?: Record<
    string,
    {
      onScroll: () => void;
      nextPageIsLoading: boolean;
      hasNextPage: boolean;
    }
  >;
  searchTexts?: Record<string, string>;
}

export default function useFilters({
  filtersSchema,
  filters,
  onFilterChange,
  onSearchAutocomplete,
  loaders,
  selectFunctions,
  searchTexts,
  onScrollFunctions,
}: UseFiltersProps): {
  filtersComponent: {
    element: Record<string, React.JSX.Element>;
    list: Record<string, SelectItemProps[]>;
    withOverflow: Record<string, any>;
    withDateOverflow: Record<string, any>;
  };
  setValues: (name: string, values: SelectItemProps[]) => void;
  control: Control;
  handleSubmit: UseFormHandleSubmit<Filters>;
  reset: UseFormReset<Filters>;
} {
  const [autocompleteLists, setAutocompleteLists] = useState({});

  const { control, reset, handleSubmit, watch } = useForm();

  const filterFields = watch();

  const setValues = (name: string, values: SelectItemProps[]) => {
    setAutocompleteLists((prevState) => ({
      ...prevState,
      [name]: values,
    }));
  };

  const defaultValues: Filters = useMemo(
    () =>
      filtersSchema.reduce((acc, cur) => {
        const obj: Filters = { ...acc };

        obj[cur.name] = {
          value: [],
        };

        return obj;
      }, {}),
    [filtersSchema],
  );

  useEffect(() => {
    if (reset) {
      if (Object.keys(filters).length > 0) {
        reset({ ...defaultValues, ...filters });
      } else {
        reset(defaultValues);
      }
    }
  }, [reset, filters, defaultValues]);

  const filtersComponent: {
    element: Record<string, React.JSX.Element>;
    list: Record<string, SelectItemProps[]>;
    withOverflow: Record<string, any>;
    withDateOverflow: Record<string, any>;
  } = { element: {}, list: {}, withOverflow: {}, withDateOverflow: {} };

  filtersSchema.forEach((item) => {
    if (item.type === FilterType.TEXT) {
      filtersComponent.element[item.name] = (
        <div>
          <Controller
            name={item.name}
            control={control}
            rules={{ required: false }}
            defaultValue=""
            render={({ field: { name, onChange } }) => (
              <InputText
                name={name}
                value={
                  (filterFields &&
                    filterFields[name] &&
                    filterFields[name].value) ||
                  ''
                }
                onChange={(e) =>
                  onChange({
                    value: e.target.value,
                    type: SchemaFilterType.STRING,
                  })
                }
                size={InputSize.SMALL}
                placeholder={item.placeholder || 'placeholder'}
                className={styles.filterInput}
              />
            )}
          />
          <br />
          <br />
          <div className={styles.defaultSubmitWrapper}>
            <ButtonPrimary
              size={ButtonSize.SMALL}
              type={ButtonType.SUBMIT}
              text="Apply"
            />
          </div>
        </div>
      );
    }

    if (item.type === FilterType.CHECKBOX && item.fields && filters) {
      const list = item.fields.map((field) => ({
        name: field.title,
        value: field.value as string,
      }));

      filtersComponent.list[item.name] = list;
      filtersComponent.withOverflow[item.name] = true;

      filtersComponent.element[item.name] = (
        <div className={styles.checkboxFiler}>
          <Controller
            name={item?.name}
            control={control}
            rules={{ required: false }}
            render={({ field: { name, onChange } }) => (
              <CheckboxFilter
                name={name}
                fields={item?.fields}
                values={
                  filterFields &&
                  filterFields[name] &&
                  filterFields[name]?.value?.length
                    ? filterFields[name]
                    : { value: [] }
                }
                onChange={(data) =>
                  onChange({
                    value: data,
                    type: SchemaFilterType.STRING,
                  })
                }
                type="checkbox"
                withClearButton={item.withClearButton}
              />
            )}
          />
          {!item.withClearButton && (
            <div className={styles.checkboxFilterSubmitWrapper}>
              <ButtonPrimary
                size={ButtonSize.SMALL}
                type={ButtonType.SUBMIT}
                text="Apply"
              />
            </div>
          )}
        </div>
      );
    }

    if (item.type === FilterType.RADIO && item?.fields && filters) {
      const list = item.fields.map((field) => ({
        name: field.title,
        value: field.value as string,
      }));

      filtersComponent.list[item.name] = list;
      filtersComponent.withOverflow[item.name] = true;

      filtersComponent.element[item.name] = (
        <div className={styles.checkboxFiler}>
          <Controller
            name={item?.name}
            control={control}
            rules={{ required: false }}
            render={({ field: { name, onChange } }) => (
              <CheckboxFilter
                name={name}
                fields={item?.fields}
                values={
                  filterFields && filterFields[name]?.value?.length
                    ? filterFields[name]
                    : { value: [] }
                }
                onChange={(data) =>
                  onChange({
                    value: data,
                    type: SchemaFilterType.STRING,
                  })
                }
                type="radio"
                withClearButton={item.withClearButton}
              />
            )}
          />
          {!item.withClearButton && (
            <div className={styles.checkboxFilterSubmitWrapper}>
              <ButtonPrimary
                size={ButtonSize.SMALL}
                type={ButtonType.SUBMIT}
                text="Apply"
              />
            </div>
          )}
        </div>
      );
    }

    if (item.type === FilterType.DATE_RANGE) {
      filtersComponent.withDateOverflow[item.name] = true;

      filtersComponent.element[item.name] = (
        <div>
          <Controller
            name={item.name}
            control={control}
            rules={{ required: false }}
            render={({ field: { onChange, name } }) => (
              <Rangepicker
                startDate={new Date(2000, 0, 1)}
                endDate={new Date(2030, 0, 1)}
                value={
                  filterFields && filterFields[name]
                    ? filterFields[name].value
                    : ''
                }
                onApply={(date) =>
                  onChange({
                    value: date,
                    type: SchemaFilterType.DATE_RANGE,
                  })
                }
                onReset={() =>
                  onFilterChange({ ...filters, [item.name]: { value: [] } })
                }
                twoPickers={item.pickerType === 'twoPickers'}
                withoutCustomSelector={item.withoutCustomSelector}
                withTimeSelector={item.withTimeSelector}
                withTwoTimeSelector={item.withTwoTimeSelector}
                filterPlace
              />
            )}
          />
        </div>
      );
    }

    if (item.type === FilterType.DATE) {
      filtersComponent.withDateOverflow[item.name] = true;

      filtersComponent.element[item.name] = (
        <div>
          <Controller
            name={item.name}
            control={control}
            rules={{ required: false }}
            render={({ field: { onChange, name } }) => (
              <Rangepicker
                startDate={new Date(1920, 0, 1)}
                endDate={new Date(2050, 0, 1)}
                onSingleApply={(date) =>
                  onChange({ value: [date], type: SchemaFilterType.DATE })
                }
                onReset={() =>
                  onFilterChange({ ...filters, [item.name]: { value: [] } })
                }
                mode={DatePickerMode.SINGLE}
                value={
                  filterFields && filterFields[name]
                    ? filterFields[name].value
                    : []
                }
                twoPickers={item.pickerType === 'twoPickers'}
                filterPlace
              />
            )}
          />
        </div>
      );
    }

    if (item.type === FilterType.AUTOCOMPLETE) {
      filtersComponent.list[item.name] = (
        autocompleteLists as Record<string, SelectItemProps[]>
      )[item.name];

      filtersComponent.element[item.name] = (
        <div>
          <Controller
            name={item.name}
            control={control}
            rules={{ required: false }}
            defaultValue=""
            render={({ field: { name, value, onChange } }) => (
              <Autocomplete
                name={name}
                value={
                  (filterFields &&
                    filterFields[name] &&
                    filterFields[name].value &&
                    filterFields[name].value[0]) ||
                  ''
                }
                onChange={(e) =>
                  onChange({
                    value: e.target.value,
                    type: SchemaFilterType.STRING,
                  })
                }
                list={
                  (autocompleteLists as Record<string, SelectItemProps[]>)[
                    item.name
                  ] || []
                }
                placeholder={item.placeholder || 'placeholder'}
                size={SelectSize.SMALL}
                className={styles.filterInput}
                inputWrapperClassName={styles.autocompleteFilterInput}
                onSearch={(searchText) => {
                  if (onSearchAutocomplete) {
                    onSearchAutocomplete(item.name, searchText);
                  }

                  if (!searchText && value) {
                    onChange({
                      target: { name, value: '' },
                    });
                  }
                }}
                withClearButton
              />
            )}
          />
          <br />
          <br />
          <div className={styles.defaultSubmitWrapper}>
            <ButtonPrimary
              size={ButtonSize.SMALL}
              type={ButtonType.SUBMIT}
              text="Apply"
            />
          </div>
        </div>
      );
    }

    if (item.type === FilterType.AUTOCOMPLETE_MULTIPLE) {
      filtersComponent.list[item.name] = (
        autocompleteLists as Record<string, SelectItemProps[]>
      )[item.name];

      filtersComponent.element[item.name] = (
        <div className={styles.autocompleteMultipleFilter}>
          <Controller
            name={item.name}
            control={control}
            rules={{ required: false }}
            defaultValue=""
            render={({ field: { name, value, onChange } }) => (
              <AutocompleteMultiple
                name={name}
                value={
                  filterFields &&
                  filterFields[name] &&
                  filterFields[name]?.value?.length
                    ? filterFields[name]?.value
                    : []
                }
                onInfinateScroll={() => {
                  if (onScrollFunctions && onScrollFunctions[item.name]) {
                    onScrollFunctions[item.name].onScroll();
                  }
                }}
                withInfiniteScroll={Boolean(
                  onScrollFunctions &&
                  onScrollFunctions[item.name] &&
                  onScrollFunctions &&
                  onScrollFunctions[item.name].onScroll,
                )}
                hasNextPage={Boolean(
                  onScrollFunctions &&
                  onScrollFunctions[item.name] &&
                  onScrollFunctions &&
                  onScrollFunctions[item.name].hasNextPage,
                )}
                nextPageIsLoading={Boolean(
                  onScrollFunctions &&
                  onScrollFunctions[item.name] &&
                  onScrollFunctions &&
                  onScrollFunctions[item.name].nextPageIsLoading,
                )}
                optionListClassName={styles.autocompleteMultipleList}
                onChange={(e) => {
                  onChange({
                    value: e.target.value,
                    type: SchemaFilterType.STRING,
                  });

                  if (selectFunctions && selectFunctions[item.name]) {
                    const nextList: SelectItemProps[] = [];

                    selectFunctions[item.name].selectedList?.forEach(
                      (listItem) => {
                        if (
                          (e.target.value as string[]).find(
                            (valueItem: string) => valueItem === listItem.value,
                          )
                        ) {
                          nextList.push(listItem);
                        }
                      },
                    );

                    (e.target.value as string[])?.forEach(
                      (listItem: string) => {
                        if (
                          !nextList?.find(
                            (selectedItem) => selectedItem.value === listItem,
                          )
                        ) {
                          nextList.push({
                            name: (
                              autocompleteLists as Record<
                                string,
                                SelectItemProps[]
                              >
                            )[item.name]?.find(
                              (autocompleteListItem) =>
                                autocompleteListItem.value === listItem,
                            )?.name as string,
                            value: listItem,
                          });
                        }
                      },
                    );

                    selectFunctions[item.name].setSelectedList(nextList);

                    if (
                      onSearchAutocomplete &&
                      searchTexts &&
                      searchTexts[item.name]
                    ) {
                      onSearchAutocomplete(item.name, '');
                    }
                  }
                }}
                list={
                  (autocompleteLists as Record<string, SelectItemProps[]>)[
                    item.name
                  ]
                }
                placeholder={item.placeholder || 'placeholder'}
                size={SelectSize.SMALL}
                isLoading={loaders ? loaders[item.name] : false}
                onSearch={(searchText) => {
                  if (onSearchAutocomplete) {
                    onSearchAutocomplete(item.name, searchText);
                  }

                  if (!searchText && value) {
                    onChange({
                      target: { name, value: '' },
                    });

                    onFilterChange({
                      ...filters,
                      [item.name]: {
                        value: [],
                      },
                    });
                  }
                }}
                contentButtons={
                  <div className={styles.buttonsWrapper}>
                    <ButtonPrimary
                      size={ButtonSize.SMALL}
                      type={ButtonType.SUBMIT}
                      onClick={() => {
                        if (
                          onSearchAutocomplete &&
                          searchTexts &&
                          searchTexts[item.name]
                        ) {
                          onSearchAutocomplete(item.name, '');
                        }
                      }}
                      text="Apply"
                    />
                    {reset && value && value.value.length > 0 && (
                      <ButtonPseudo
                        text="Clear"
                        className={styles.buttonClear}
                        size={ButtonSize.SMALL}
                        onClick={() => {
                          const nextFilters = {
                            ...filters,
                            [item.name]: {
                              value: [],
                            },
                          };

                          reset(nextFilters);
                          onFilterChange(nextFilters);

                          if (
                            onSearchAutocomplete &&
                            searchTexts &&
                            searchTexts[item.name]
                          ) {
                            onSearchAutocomplete(item.name, '');
                          }
                        }}
                      />
                    )}
                  </div>
                }
              />
            )}
          />
          <br />
          <div className={styles.defaultSubmitWrapper}>
            <ButtonPrimary
              size={ButtonSize.SMALL}
              type={ButtonType.SUBMIT}
              onClick={() => {
                if (
                  onSearchAutocomplete &&
                  searchTexts &&
                  searchTexts[item.name]
                ) {
                  onSearchAutocomplete(item.name, '');
                }
              }}
              text="Apply"
            />
            {reset && (
              <ButtonPseudo
                text="Clear"
                className={styles.buttonClear}
                size={ButtonSize.SMALL}
                onClick={() => {
                  const nextFilters = {
                    ...filters,
                    [item.name]: {
                      value: [],
                    },
                  };

                  reset(nextFilters);
                  onFilterChange(nextFilters);

                  if (
                    onSearchAutocomplete &&
                    searchTexts &&
                    searchTexts[item.name]
                  ) {
                    onSearchAutocomplete(item.name, '');
                  }
                }}
              />
            )}
          </div>
        </div>
      );
    }
  });

  return {
    filtersComponent,
    setValues,
    control,
    handleSubmit,
    reset,
  };
}
